import { Component, Input, computed, signal, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  HeaderSettings,
  HeaderContent,
  HeaderLayoutVariant,
  NavLink,
  SECTION_THEME,
} from './section-renderer.models';
import { HeaderStateService } from '../services/header-state.service';
import { PublicI18nService } from '@frontend/public-calendar';

/**
 * Header Section Renderer
 * Full-width header/navigation sections with multiple layout variants.
 * Based on Tailwind Plus header examples.
 */
@Component({
  selector: 'dcp-header-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <header 
      [class.sticky]="settings().sticky"
      [class.top-0]="settings().sticky"
      [class.z-50]="settings().sticky"
      [style.background]="backgroundStyle()"
    >
      @switch (layoutVariant()) {
        @case ('transparent') {
          <!-- Transparent Header (for hero overlays) -->
          <nav aria-label="Global" class="absolute inset-x-0 top-0 z-50 flex items-center justify-between p-6 lg:px-8">
            <!-- Logo -->
            <div class="flex lg:flex-1">
              @if (logoUrl()) {
                <a href="#" class="-m-1.5 p-1.5">
                  <span class="sr-only">{{ content().logoAlt || 'Company' }}</span>
                  <img [src]="logoUrl()" [alt]="content().logoAlt || ''" class="h-8 w-auto" />
                </a>
              } @else if (content().logoText) {
                <span class="text-lg font-semibold" [style.color]="theme.textColor">{{ content().logoText }}</span>
              }
            </div>
            
            <!-- Mobile menu button -->
            <div class="flex lg:hidden">
              <button 
                type="button" 
                class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5"
                [style.color]="theme.textMutedColor"
                (click)="openMobileMenu()"
              >
                <span class="sr-only">Open main menu</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" class="size-6">
                  <path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
            </div>
            
            <!-- Navigation Links -->
            @if (settings().showNavLinks !== false && navLinks().length) {
              <div class="hidden lg:flex lg:gap-x-12">
                @for (link of navLinks(); track $index) {
                  <a 
                    [href]="getNavLinkHref(link)" 
                    class="text-sm/6 font-semibold"
                    [style.color]="theme.textColor"
                  >
                    {{ getNavLinkLabel(link) }}
                  </a>
                }
              </div>
            }
            
            <!-- Language Toggle + CTA -->
            <div class="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center lg:gap-x-4">
              <!-- Language Toggle -->
              <div class="flex items-center gap-1 text-sm">
                <button
                  type="button"
                  (click)="setLanguage('en')"
                  [class]="i18n.locale() === 'en' ? 'px-2 py-1 rounded font-medium' : 'px-2 py-1 rounded hover:opacity-80'"
                  [style.color]="i18n.locale() === 'en' ? theme.primaryColor : theme.textMutedColor"
                >EN</button>
                <span [style.color]="theme.textMutedColor">|</span>
                <button
                  type="button"
                  (click)="setLanguage('de')"
                  [class]="i18n.locale() === 'de' ? 'px-2 py-1 rounded font-medium' : 'px-2 py-1 rounded hover:opacity-80'"
                  [style.color]="i18n.locale() === 'de' ? theme.primaryColor : theme.textMutedColor"
                >DE</button>
              </div>
              
              @if (settings().showCtaButton && content().ctaText) {
                <a 
                  [href]="content().ctaUrl || '#'" 
                  class="rounded-md px-3.5 py-2 text-sm font-semibold shadow-sm transition-colors"
                  [style.background-color]="theme.primaryColor"
                  [style.color]="theme.buttonTextColor"
                >
                  {{ content().ctaText }}
                </a>
              }
            </div>
          </nav>
        }
        
        @default {
          <!-- Simple Layout -->
          <nav aria-label="Global" class="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8">
            <!-- Logo -->
            <div class="flex lg:flex-1">
              @if (logoUrl()) {
                <a href="#" class="-m-1.5 p-1.5">
                  <span class="sr-only">{{ content().logoAlt || 'Company' }}</span>
                  <img [src]="logoUrl()" [alt]="content().logoAlt || ''" class="h-8 w-auto" />
                </a>
              } @else if (content().logoText) {
                <span class="text-lg font-semibold" [style.color]="theme.textColor">{{ content().logoText }}</span>
              }
            </div>
            
            <!-- Mobile menu button -->
            <div class="flex lg:hidden">
              <button 
                type="button" 
                class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5"
                [style.color]="theme.textMutedColor"
                (click)="openMobileMenu()"
              >
                <span class="sr-only">Open main menu</span>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true" class="size-6">
                  <path d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
            </div>
            
            <!-- Navigation Links -->
            @if (settings().showNavLinks !== false && navLinks().length) {
              <div class="hidden lg:flex lg:gap-x-12">
                @for (link of navLinks(); track $index) {
                  <a 
                    [href]="getNavLinkHref(link)" 
                    class="text-sm/6 font-semibold"
                    [style.color]="theme.textColor"
                  >
                    {{ getNavLinkLabel(link) }}
                  </a>
                }
              </div>
            }
            
            <!-- Language Toggle + CTA -->
            <div class="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center lg:gap-x-4">
              <!-- Language Toggle -->
              <div class="flex items-center gap-1 text-sm">
                <button
                  type="button"
                  (click)="setLanguage('en')"
                  [class]="i18n.locale() === 'en' ? 'px-2 py-1 rounded font-medium' : 'px-2 py-1 rounded hover:opacity-80'"
                  [style.color]="i18n.locale() === 'en' ? theme.primaryColor : theme.textMutedColor"
                >EN</button>
                <span [style.color]="theme.textMutedColor">|</span>
                <button
                  type="button"
                  (click)="setLanguage('de')"
                  [class]="i18n.locale() === 'de' ? 'px-2 py-1 rounded font-medium' : 'px-2 py-1 rounded hover:opacity-80'"
                  [style.color]="i18n.locale() === 'de' ? theme.primaryColor : theme.textMutedColor"
                >DE</button>
              </div>
              
              @if (settings().showCtaButton && content().ctaText) {
                <a 
                  [href]="content().ctaUrl || '#'" 
                  class="rounded-md px-3.5 py-2 text-sm font-semibold shadow-sm transition-colors"
                  [style.background-color]="theme.primaryColor"
                  [style.color]="theme.buttonTextColor"
                >
                  {{ content().ctaText }}
                </a>
              }
            </div>
          </nav>
        }
      }
    </header>
  `,
})
export class HeaderRendererComponent implements OnInit, OnDestroy {
  private readonly headerState = inject(HeaderStateService);
  protected readonly i18n = inject(PublicI18nService);
  
  /** Theme injected from parent fullwidth-preview */
  private readonly themeSignal = inject(SECTION_THEME);
  protected get theme() { return this.themeSignal(); }

  /** Section settings */
  @Input() set settingsInput(value: Record<string, unknown>) {
    this._settings.set(value as HeaderSettings);
  }
  private _settings = signal<HeaderSettings>({});
  readonly settings = this._settings.asReadonly();

  /** Section content */
  @Input() set contentInput(value: Record<string, unknown>) {
    this._content.set(value as HeaderContent);
  }
  private _content = signal<HeaderContent>({});
  readonly content = this._content.asReadonly();

  /** Computed layout variant with fallback */
  readonly layoutVariant = computed<HeaderLayoutVariant>(() => {
    return (this._settings().layoutVariant as HeaderLayoutVariant) || 'simple';
  });

  /** Computed logoUrl - reads from settings first (resolved from logoImageId), falls back to content */
  readonly logoUrl = computed<string | undefined>(() => {
    const settingsLogoUrl = this._settings().logoUrl;
    if (settingsLogoUrl) {
      return settingsLogoUrl;
    }
    // Fallback to content for legacy data
    return this._content().logoUrl;
  });

  /** Computed navLinks - reads from settings first, falls back to content for backward compatibility */
  readonly navLinks = computed<NavLink[]>(() => {
    const settingsLinks = this._settings().navLinks;
    if (settingsLinks && settingsLinks.length > 0) {
      return settingsLinks;
    }
    // Fallback to content for legacy data
    return this._content().navLinks || [];
  });

  /** Computed background color (legacy, kept for compatibility) */
  readonly backgroundColor = computed(() => {
    const settings = this._settings();
    if (settings.backgroundType === 'solid' && settings.backgroundColor) {
      return settings.backgroundColor;
    }
    return this.themeSignal()?.surfaceColor || '#ffffff';
  });

  /** Computed background style (supports solid, gradient, transparent, inherit) */
  readonly backgroundStyle = computed(() => {
    const settings = this._settings();
    const bgType = settings.backgroundType;
    
    // Legacy support: check transparent boolean if backgroundType not set
    if (!bgType && settings.transparent) {
      return 'transparent';
    }
    
    switch (bgType) {
      case 'transparent':
        return 'transparent';
      case 'solid':
        return settings.backgroundColor || this.themeSignal()?.surfaceColor || '#ffffff';
      case 'gradient':
        return settings.backgroundGradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';
      case 'inherit':
      default:
        return this.themeSignal()?.surfaceColor || '#ffffff';
    }
  });

  ngOnInit(): void {
    const content = this._content();
    this.headerState.registerHeader(
      this.navLinks(),
      this.logoUrl(),
      content.logoText
    );
  }

  ngOnDestroy(): void {
    this.headerState.unregisterHeader();
  }

  openMobileMenu(): void {
    this.headerState.openMobileMenu();
  }

  setLanguage(locale: 'de' | 'en'): void {
    this.i18n.setLocale(locale);
  }

  /** Get the URL/href for a navigation link */
  getNavLinkHref(link: NavLink): string {
    // Handle scroll-to-section links
    if (link.linkType === 'scroll' && link.targetSectionAnchor) {
      return `#section-${link.targetSectionAnchor}`;
    }
    // Handle legal page links (external navigation)
    if (link.linkType === 'legal' || link.type === 'legal') {
      if (link.legalKey === 'imprint') return '/legal/imprint';
      if (link.legalKey === 'privacy') return '/legal/privacy';
    }
    // URL links or legacy
    return link.url || '#';
  }

  /** Get the display label for a navigation link */
  getNavLinkLabel(link: NavLink): string {
    const locale = this.i18n.locale();
    // Try locale-specific label first
    if (locale === 'en' && link.labelEn) return link.labelEn;
    if (locale === 'de' && link.labelDe) return link.labelDe;
    // Fallback to other language or legacy label
    return link.labelDe || link.labelEn || link.label || '';
  }
}
